import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { RequestMiss } from "shared";
import { ApiException } from "@/api/client";
import { createBlock } from "@/api/blocks";
import { getLogDetail } from "@/api/logs";
import { UaCell } from "@/components/UaCell";
import { formatDt } from "@/lib/format";

export function LogDetailPage() {
	const [params] = useSearchParams();
	const path = params.get("path") ?? "";
	const [misses, setMisses] = useState<RequestMiss[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [blockedIps, setBlockedIps] = useState<Set<string>>(new Set());

	const load = useCallback(async () => {
		if (!path) {
			setLoading(false);
			setError("Missing path");
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const res = await getLogDetail(path);
			setMisses(res.misses);
		} catch (err) {
			setError(err instanceof ApiException ? err.message : "Failed to load");
		} finally {
			setLoading(false);
		}
	}, [path]);

	useEffect(() => {
		void load();
	}, [load]);

	async function blockIp(ip: string) {
		const key = ip.toLowerCase();
		if (blockedIps.has(key)) return;
		if (!confirm(`Block IP "${ip}" globally (all links)?`)) return;
		try {
			await createBlock({ kind: "ip", value: ip });
			setBlockedIps((prev) => new Set(prev).add(key));
		} catch (err) {
			setError(err instanceof ApiException ? err.message : "Block failed");
		}
	}

	if (loading) {
		return <p className="text-fg-muted text-sm">Loading…</p>;
	}
	if (!path) {
		return (
			<div className="space-y-2">
				<p className="text-fg-muted">{error ?? "Not found"}</p>
				<Link to="/logs" className="text-accent hover:underline text-sm">
					Back
				</Link>
			</div>
		);
	}

	const totalHits = misses.reduce((sum, m) => sum + m.hitCount, 0);

	return (
		<div className="space-y-8">
			<div>
				<p className="text-sm text-fg-muted">
					<Link to="/logs" className="hover:underline">
						Logs
					</Link>{" "}
					/
				</p>
				<h1 className="mt-1 font-mono text-xl text-accent break-all">{path}</h1>
				<div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
					<span className="font-mono">
						{totalHits} hits · {misses.length} source
						{misses.length === 1 ? "" : "s"}
					</span>
					{misses[0] && (
						<>
							<span className="rounded-full bg-surface-raised px-2 py-0.5 font-medium">
								last status {misses[0].lastStatus}
							</span>
							<span>
								first seen{" "}
								{formatDt(
									misses.reduce(
										(min, m) => (m.firstSeenAt < min ? m.firstSeenAt : min),
										misses[0].firstSeenAt,
									),
								)}
							</span>
						</>
					)}
				</div>
			</div>

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			<section className="space-y-3">
				<h2 className="text-sm font-medium text-fg-muted">
					Sources (per method · query · IP)
				</h2>
				<div className="overflow-x-auto rounded-xl border border-border">
					<table className="w-full min-w-[820px] text-left text-sm">
						<thead className="border-b border-border bg-surface-raised text-fg-muted">
							<tr>
								<th className="px-3 py-2 font-medium">IP</th>
								<th className="px-3 py-2 font-medium">Country</th>
								<th className="px-3 py-2 font-medium">Method</th>
								<th className="px-3 py-2 font-medium">Query</th>
								<th className="px-3 py-2 font-medium">Browser / OS</th>
								<th className="px-3 py-2 font-medium">Referer</th>
								<th className="px-3 py-2 font-medium">Hits</th>
								<th className="px-3 py-2 font-medium">Last seen</th>
								<th className="px-3 py-2 font-medium">Actions</th>
							</tr>
						</thead>
						<tbody>
							{misses.map((m) => {
								const blocked = blockedIps.has(m.ip.toLowerCase());
								return (
									<tr key={m.id} className="border-b border-border last:border-0">
										<td className="px-3 py-2 font-mono text-xs">
											{m.ip ? (
												<Link
													to={`/logs/ip?ip=${encodeURIComponent(m.ip)}`}
													title="Other paths this IP probed"
													className="text-accent hover:underline"
												>
													{m.ip}
												</Link>
											) : (
												"—"
											)}
										</td>
										<td className="px-3 py-2 font-mono text-xs">
											{m.lastCountry ?? "—"}
											{m.lastAsn != null && (
												<span className="text-fg-muted"> · AS{m.lastAsn}</span>
											)}
										</td>
										<td className="px-3 py-2 font-mono text-xs">{m.method}</td>
										<td
											className="max-w-[10rem] truncate px-3 py-2 font-mono text-xs text-fg-muted"
											title={m.query}
										>
											{m.query || "—"}
										</td>
										<UaCell ua={m.lastUserAgent} />
										<td
											className="max-w-[12rem] truncate px-3 py-2 text-xs text-fg-muted"
											title={m.lastReferer ?? undefined}
										>
											{m.lastReferer ?? "—"}
										</td>
										<td className="px-3 py-2 font-mono text-xs tabular-nums">
											{m.hitCount}
										</td>
										<td className="px-3 py-2 text-xs text-fg-muted whitespace-nowrap">
											{formatDt(m.lastSeenAt)}
										</td>
										<td className="px-3 py-2">
											<button
												type="button"
												disabled={!m.ip || blocked}
												className="text-xs text-fg-muted hover:text-rose-600 disabled:opacity-40 dark:disabled:text-rose-400/40 disabled:hover:text-fg-muted"
												onClick={() => void blockIp(m.ip)}
											>
												{blocked ? "IP blocked" : "Block IP"}
											</button>
										</td>
									</tr>
								);
							})}
							{misses.length === 0 && (
								<tr>
									<td colSpan={9} className="px-3 py-6 text-center text-fg-muted">
										No misses recorded for this path
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
}
